<template>
  <div v-if="showCheckerOnPage" class="compatibilityWrapper">
    <h3 class="compatibilityHeading hdg">Check for Compatibility</h3>
    <p class="compatibilityDescription hdg">
      Browse compatible manufacturers and models below to learn if this mount is
      compatible with your device.
    </p>

    <DropDownWithSearch
      searchPlaceholder="Search"
      :dropDownId="manufactureDropdownId"
      :dropDownMsg="manufacturerText"
      :showManufacturerMenu="showManufacturerMenu"
      :showModelMenu="showModelsMenu"
      :isLoading="manufacturerMenuLoading"
      :manufacturerSelectedByUser="manufacturerSelectedByUser"
      dataAttributeManufacturer="data-compatibility-checker-search-manufacturer"
      analyticsClassManufacturer="compatibility-checker-search-manufacturer"
      dataAttributeSelectedManufacturer="data-compatibility-checker-result-manufacturer"
      analyticsClassSelectedManufacturer="compatibility-checker-result-manufacture"
      @send-refs="setMountedRefs"
      @filterText="filterManufacturerDropdown"
      @filterModelText="filterModelDropdown"
      @clearManufacturer="clearManufacturerSelection"
    >
      <template v-slot:manufacturers>
        <div
          v-for="manufacturer in filteredManufacturerArray"
          :key="manufacturer.ManufacturerID"
          v-on:copy="doNotCopyElementText"
          v-on:cut="doNotCopyElementText"
        >
          <SearchOption
            :text="manufacturer.ManufacturerName"
            :dropDownId="manufactureDropdownId"
            :optionData="manufacturer.ManufacturerID"
            @optionClicked="manufactureOptionClicked"
            analyticsClass="compatibility-checker-search-manufacturer"
            dataAttribute="data-compatibility-checker-search-manufacturer"
            :dataAttributeValue="manufacturer.ManufacturerName"
            :isManufacturerOption="true"
          />
        </div>
      </template>
      <template v-slot:models>
        <div
          v-for="model in filteredModelArray"
          :key="model.ConsumerProductID"
          v-on:copy="doNotCopyElementText"
          v-on:cut="doNotCopyElementText"
        >
          <SearchOptionWithRequiredPart
            v-if="true === model.RequiredProductExists"
            :dropDownId="modelDropdownId"
            :text="model.Model"
            :requiredPartQuantity="model.RequiresProductQuantity"
            :requiredPartName="model.RequiredProduct"
            :requiredPartUrl="model.RequiredProductURL"
            :productSkuName="getSelectedSkuCode"
            analyticsClass="compatibility-checker-result-model-number"
            dataAttribute="data-compatibility-checker-result-model-number"
            :dataAttributeValue="model.Model"
            analyticsClassRequiredProduct="compatibility-checker-required-result-link-model-number"
            dataAttributeRequiredProduct="data-compatibility-checker-result-model-number"
            :dataAttributeValueRequiredProduct="model.RequiredProduct"
            @optionClicked="modelOptionClicked"
          />
          <SearchOption
            v-else
            :text="model.Model"
            :dropDownId="modelDropdownId"
            @optionClicked="modelOptionClicked"
          />
        </div>
      </template>
    </DropDownWithSearch>
  </div>
</template>

<script>
import DropDownWithSearch from "./options/DropDownWithSearch.vue";
import SearchOptionWithRequiredPart from "./options/SearchOptionWithRequiredPart.vue";
import SearchOption from "./options/SearchOption.vue";
import ProductCompatibilityCheckerService from "../../services/ProductCompatibilityCheckerService";

import { mapState, mapActions, mapGetters } from "vuex";
// manufacturer logic
const DEFAULT_MANUFACTURER_TEXT = "Select your manufacturer";
let isListenerSet = false;

export default {
  data() {
    return {
      dropDownWithSearchRefs: null,
      manufacturersAreLoading: false,
      isModelsMenuOpen: false
    };
  },
  directives: {
    clickoutside: {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          const store = vnode.context.$store;
          let isManufacturerMenuOpen =
            store.getters["compatibilityChecker/getShowManufacturerMenu"];

          let isModelsMenuOpen =
            store.getters["compatibilityChecker/getShowModelsMenu"];

          event.stopPropagation();

          const isMenuOpen = isManufacturerMenuOpen || isModelsMenuOpen;
          const isClickOutsideOfDropdown = !event.target.closest(
            ".vue-dropdown-with-search"
          );
          const shouldCloseMenu = isMenuOpen && isClickOutsideOfDropdown;
          if (shouldCloseMenu) {
            vnode.context[binding.expression](event);
          }
        };

        if (!isListenerSet) {
          document.addEventListener("click", el.clickOutsideEvent);
          isListenerSet = true;
        }
      },
      unbind: function(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
      stopProp(event) {
        event.stopPropagation();
      }
    }
  },
  created() {},
  beforeUnmount() {},
  computed: {
    ...mapState("compatibilityChecker", [
      "showManufacturerMenu",
      "manufacturerMenuLoading",
      "showModelsMenu",
      "selectedManufacturer"
    ]),
    ...mapGetters("compatibilityChecker", [
      "getLoadedManufacturers",
      "filteredManufacturerArray",
      "filteredModelArray"
    ]),
    ...mapGetters("product", [
      "isSkuPage",
      "getProductBrandDetails",
      "getSelectedSkuCode"
    ]),
    manufacturerText() {
      if (this.selectedManufacturer) {
        return this.selectedManufacturer;
      } else {
        return DEFAULT_MANUFACTURER_TEXT;
      }
    },
    manufacturerSelectedByUser() {
      return this.selectedManufacturer != null;
    },
    manufactureDropdownId() {
      return "manufacturer";
    },
    modelDropdownId() {
      return "model";
    },
    showCheckerOnPage() {
      const shouldShowChecker = ProductCompatibilityCheckerService.isProductChiefMount(
        this.getProductBrandDetails,
        this.isSkuPage
      );

      const manufacturerOptionsHaveLoaded =
        null !== this.getLoadedManufacturers;

      const manufacturersHaveLoaded = false === this.manufacturersAreLoading;

      const isLoadedManufacturersHasValue =
        this.getLoadedManufacturers?.length > 0;

      return (
        shouldShowChecker &&
        manufacturersHaveLoaded &&
        manufacturerOptionsHaveLoaded &&
        isLoadedManufacturersHasValue
      );
    },
    shouldLoadManufacturers() {
      const shouldShowChecker = ProductCompatibilityCheckerService.isProductChiefMount(
        this.getProductBrandDetails,
        this.isSkuPage
      );
      return shouldShowChecker;
    }
  },
  methods: {
    ...mapActions("compatibilityChecker", [
      "updateIsManufacturerMenuLoading",
      "closeManufacturerMenu",
      "loadManufacturers",
      "clearLoadedManufacturers",
      "loadCompatibleProducts",
      "clearCompatibleProducts",
      "openModelsMenu",
      "userSelectManufacturer",
      "updateManufacturerFilterText",
      "updateModelFilterText",
      "setModelAnalytics",
      "closeModelsMenu"
    ]),
    manufactureOptionClicked(msg) {
      const { value, optionData, event } = msg;
      event.stopPropagation();
      if (value) {
        this.userSelectManufacturer(value);
        const productCode = this.getSelectedSkuCode;
        const manufacturerID = optionData;
        // call api and handle UI
        this.updateIsManufacturerMenuLoading(true);
        ProductCompatibilityCheckerService.getCompatibilityProducts(
          productCode,
          manufacturerID
        )
          .then(res => res.data)
          .then(products => {
            this.loadCompatibleProducts(products);
          })
          .catch(() => {
            // to handle error
            this.clearCompatibleProducts();
          })
          .finally(() => {
            this.updateIsManufacturerMenuLoading(false);
            this.closeManufacturerMenu();
            this.openModelsMenu();
            this.focusOnModelInput();
          });
      }
    },
    async closeMenu() {
      this.closeManufacturerMenu();
      this.closeModelsMenu();
      this.filterManufacturerDropdown("");
      this.filterModelDropdown("");
      this.userSelectManufacturer(null);
    },
    modelOptionClicked(msg) {
      const { value, event } = msg;
      event.stopPropagation();
      const modelValue = value;
      if (modelValue) {
        this.setModelAnalytics(modelValue);
      }
    },
    focusOnDropdown() {
      this.$nextTick(() => {
        this.dropDownWithSearchRefs.refs.buttonElement.focus();
      });
    },
    focusOnModelInput() {
      this.$nextTick(() => {
        this.dropDownWithSearchRefs.refs.searchModelInput.focus();
      });
    },
    setMountedRefs(refs) {
      this.dropDownWithSearchRefs = refs;
    },
    filterManufacturerDropdown(text) {
      this.updateManufacturerFilterText(text);
    },
    filterModelDropdown(text) {
      this.updateModelFilterText(text);
    },
    clearManufacturerSelection() {
      this.userSelectManufacturer(null);
    },
    callLoadManufacturers() {
      const productCode = this.getSelectedSkuCode;
      this.manufacturersAreLoading = true;
      ProductCompatibilityCheckerService.getCompatibilityManufacturerList(
        productCode
      )
        .then(res => res.data)
        .then(manufacturers => {
          this.loadManufacturers(manufacturers);
        })
        .catch(() => {
          this.clearLoadedManufacturers();
        })
        .finally(() => {
          this.manufacturersAreLoading = false;
        });
    },
    loadManufacturersOnMount() {
      if (true === this.shouldLoadManufacturers) {
        this.callLoadManufacturers();
      } else {
        return;
      }
    },
    doNotCopyElementText(event) {
      event.preventDefault();
    }
  },
  watch: {
    // This code watches for user changing sku once the component has loaded and fetches manufactures for the updated sku
    getSelectedSkuCode(newSelectedSku, oldSelectedSku) {
      const skuExistsAndHasChanged =
        newSelectedSku && newSelectedSku !== oldSelectedSku;
      if (skuExistsAndHasChanged && this.shouldLoadManufacturers) {
        this.callLoadManufacturers();
      }
    }
  },

  components: {
    DropDownWithSearch,
    SearchOption,
    SearchOptionWithRequiredPart
  }
};
</script>

<style lang="scss" scoped>
.compatibilityWrapper {
  margin-top: 32px;
}
.compatibilityHeading {
  text-align: center;
  margin-bottom: 12px;
  color: $black85;
  @include heading6();
  @media (min-width: $media-min-width-desktop) {
    @include heading5();
  }
}

.compatibilityDescription {
  text-align: center;
  margin-bottom: 16px;
  color: $darkgray;
  @include caption();
  @media (min-width: $media-min-width-desktop) {
    @include body_small();
  }
}
</style>
